// Шапка «‹ / иконка / название / описание» + форма по типу (ref SubBlockSettingsEditor:423-442).
// Форма есть у КАЖДОГО типа юниона; легаси-типы бека (`button`/`divider`/`animation`)
// студия не редактирует — для них шапка честно показывает сырой тип и говорит, что
// редактора нет, вместо пустой шапки с пустыми span'ами.
import { ChevronLeft, HelpCircle } from 'lucide-react'
import type { DesignElement } from '@/types/blocks'
import type { DesignElementWithoutUi } from '@/features/editor/lib/designElements'
import { BadgeSettings } from './BadgeSettings'
import { DESIGN_ELEMENT_CARDS } from './designElementCards'
import { HeadingSettings } from './HeadingSettings'
import { ImageSettings } from './ImageSettings'
import { TableSettings } from './TableSettings'
import { TextSettings } from './TextSettings'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  element: DesignElement
  onUpdate: (patch: Partial<DesignElement>) => void
  onClose: () => void
}

const SettingsForm = ({ element, onUpdate }: Omit<Props, 'onClose'>) => {
  const m = useMessages(messages)
  if (element.type === 'heading') return <HeadingSettings element={element} onUpdate={onUpdate} />
  if (element.type === 'text') return <TextSettings element={element} onUpdate={onUpdate} />
  if (element.type === 'image') return <ImageSettings element={element} onUpdate={onUpdate} />
  if (element.type === 'badge') return <BadgeSettings element={element} onUpdate={onUpdate} />
  if (element.type === 'table') return <TableSettings element={element} onUpdate={onUpdate} />
  // Гейт исчерпанности (тот же, что в реестре рендеров): сюда доезжает только тип,
  // форму которого мы не делаем намеренно (сейчас таких нет — `never`). Забыли
  // форму для нового типа юниона — упадёт компиляция.
  const _exhaustive: DesignElementWithoutUi = element
  return (
    <p data-testid="design-settings-unsupported" className="text-xs text-muted-foreground">
      {m.unsupportedElement}
    </p>
  )
}

export const DesignElementSettings = ({ element, onUpdate, onClose }: Props) => {
  const m = useMessages(messages)
  const card = DESIGN_ELEMENT_CARDS.find((c) => c.type === element.type)
  // Без карточки (легаси-тип) в шапке был пустой квадрат и два пустых span'а —
  // подставляем сырой тип, чтобы автор понимал, что за элемент выбран.
  const Icon = card?.icon ?? HelpCircle
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 border-b border-border pb-3">
        <button
          type="button"
          onClick={onClose}
          aria-label={m.backToElements}
          className="rounded-lg p-1.5 transition-colors hover:bg-muted"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-2">
          {/* Иконка без подложки и крупнее — как в шапке панели блока. */}
          <Icon className="h-6 w-6 text-foreground" strokeWidth={1.75} />
          <span>
            <span data-testid="design-settings-title" className="block text-base font-semibold">
              {card?.label ?? element.type}
            </span>
            {/* Подпись осталась только у незнакомого типа: там она — единственное объяснение. */}
            {card ? null : (
              <span className="block text-xs text-muted-foreground">{m.unsupportedType}</span>
            )}
          </span>
        </div>
      </div>
      <SettingsForm element={element} onUpdate={onUpdate} />
    </div>
  )
}
